import { computed, inject, Injectable } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, forkJoin, map, Observable, of } from "rxjs";
import { HttpDataService } from "src/services/httpdata.service";
import { environment } from "src/environments/environment";
import { NewDwellingDataService } from "src/app/infopage/services/new-dwelling-data.service";
import { INewDwellingProspectUnit, INewDwellingProspectUnits, INewDwellingUnitStatistics } from "../new-new-dwelling/newdwelling.model";

/** One stage's prospect units, tagged with the stage they belong to. */
export interface StageProspectUnits {
    projectStageEstateTypeId: number;
    units: INewDwellingProspectUnit[];
    totals: INewDwellingUnitStatistics | null;
}

/**
 * Prospect units for a whole project, assembled by fetching every stage.
 */
@Injectable()
export class ProjectSalesPaceService {
    private readonly http = inject(HttpDataService);
    private readonly newDwellingService = inject(NewDwellingDataService);

    private readonly stageIdKey = computed<string>(() => {
        if (this.newDwellingService.pseid() != null) {
            return "";
        }
        return this.newDwellingService
            .stages()
            .map(stage => stage.projectStageEstateTypeId)
            .filter(id => id != null)
            .join(",");
    });

    private readonly resource = rxResource({
        params: () => this.stageIdKey(),
        stream: ({ params: key }) => {
            if (!key) {
                return of<StageProspectUnits[]>([]);
            }
            return forkJoin(key.split(",").map(id => this.getTaggedStageUnits(Number(id))));
        },
    });

    readonly isLoading = computed<boolean>(() => {
        if (this.newDwellingService.pseid() != null) {
            return false;
        }
        if (!this.newDwellingService.projectData()?.projectInfo) {
            return true;
        }
        return this.resource.isLoading();
    });

    readonly stageGroups = computed<StageProspectUnits[]>(() => (this.resource.hasValue() ? this.resource.value() : []));

    readonly unitsByStage = computed<Map<number, StageProspectUnits>>(
        () => new Map(this.stageGroups().map(group => [group.projectStageEstateTypeId, group]))
    );

    readonly units = computed<INewDwellingProspectUnit[]>(() => this.stageGroups().flatMap(group => group.units));

    private getTaggedStageUnits(projectStageEstateTypeId: number): Observable<StageProspectUnits> {
        return this.http
            .get<{ data: INewDwellingProspectUnits }>(`${environment.apiUrl}/NewDwelling/${projectStageEstateTypeId}/NewDwellingProspectUnits`)
            .pipe(
                map(response => ({
                    projectStageEstateTypeId,
                    units: response?.data?.units ?? [],
                    totals: response?.data?.totalStatisticsValues ?? null,
                })),
                catchError(() => of({ projectStageEstateTypeId, units: [], totals: null }))
            );
    }
}
